import React, { useState } from 'react';
import { X, Mail, Lock, User, Shield, ArrowRight, UserCheck, AlertCircle, Sparkles, CheckCircle2, RefreshCw, Bot, KeyRound, ShieldCheck, ArrowLeft, MessageSquare, Smartphone, Key, Zap, Copy, Check } from 'lucide-react';
import { UserRole, UserProfile } from '../types';
import { Logo } from './Logo';
import { db, MASTER_ADMIN_USER, ensureAdminUserExists } from '../lib/firebase';
import { doc, setDoc, deleteDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { sendWhatsAppNotification } from '../lib/whatsapp';
import { CountryPhoneInput } from './CountryPhoneInput';
import { getStoredReferralSuite, registerReferralForNewUser } from '../lib/affiliatesAndGiveaways';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (user: UserProfile) => void;
  demoUsers: UserProfile[];
  onUsersUpdate?: (users: UserProfile[]) => void;
}

// Common disposable / spam bot email domains to block
const DISPOSABLE_EMAIL_DOMAINS = [
  'tempmail.com', 'mailinator.com', '10minutemail.com', 'guerrillamail.com',
  'sharklasers.com', 'dispostable.com', 'getnada.com', 'trashmail.com',
  'yopmail.com', 'throwawaymail.com', 'fakeinbox.com', 'burnermail.io'
];

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  onLoginSuccess,
  demoUsers,
  onUsersUpdate
}) => {
  const [mode, setMode] = useState<'login' | 'register' | 'verify' | 'forgot_password' | 'reset_password'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole>('client');
  const [isHuman, setIsHuman] = useState(false);
  
  // Verification Code State
  const [generatedCode, setGeneratedCode] = useState('');
  const [inputCode, setInputCode] = useState('');
  const [pendingUser, setPendingUser] = useState<UserProfile | null>(null);

  // Forgot Password / Reset Password State
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCodeGenerated, setResetCodeGenerated] = useState('');
  const [resetCodeInput, setResetCodeInput] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetUser, setResetUser] = useState<UserProfile | null>(null);

  // Smartphone / Phone State
  const [countryDdi, setCountryDdi] = useState('+55');
  const [registerPhone, setRegisterPhone] = useState('');
  const [referrerSuiteCode, setReferrerSuiteCode] = useState(() => getStoredReferralSuite());

  // WhatsApp state
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);

  const handleSendWhatsAppCode = async (phone: string, codeToSend: string, isReset = false) => {
    if (!phone || !phone.trim()) {
      setError('Por favor, informe seu número de WhatsApp com DDD.');
      return;
    }
    setIsSendingWhatsApp(true);
    setError(null);
    try {
      const title = isReset ? 'Código de Recuperação de Senha' : 'Código de Ativação do Cadastro';
      const message = isReset
        ? `Seu código para redefinir sua senha no Do Japão é: *${codeToSend}*`
        : `Seu código de verificação para ativar sua Suíte Do Japão é: *${codeToSend}*`;
      
      const res = await sendWhatsAppNotification(phone.trim(), title, message);
      if (res.success) {
        if (res.simulated) {
          setInfoMessage(`📱 Código gerado: ${codeToSend}. (Ambiente em simulação)`);
        } else {
          setInfoMessage(`✅ Código enviado com sucesso para o WhatsApp ${phone.trim()}!`);
        }
      } else {
        setInfoMessage(`📱 Código gerado: ${codeToSend}.`);
      }
    } catch (err) {
      console.warn('Erro ao enviar WhatsApp:', err);
      setInfoMessage(`📱 Código gerado: ${codeToSend}.`);
    } finally {
      setIsSendingWhatsApp(false);
    }
  };

  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleQuickDemoSelect = (u: UserProfile) => {
    const isMasterAdmin = (u?.email || '').toLowerCase() === (MASTER_ADMIN_USER.email || '').toLowerCase() || u?.uid === MASTER_ADMIN_USER.uid;
    if (isMasterAdmin) {
      onLoginSuccess({ ...u, ...MASTER_ADMIN_USER, role: 'admin', suiteNumber: 'DJ-ADMIN' });
    } else {
      onLoginSuccess(u);
    }
    onClose();
  };

  // Calculates the next sequential suite number starting at DJ-24950
  const getNextSuiteNumber = (): string => {
    let maxNum = 24949; // Baseline so the very first client gets 24950
    demoUsers.forEach((u) => {
      if (u.suiteNumber) {
        const match = u.suiteNumber.match(/\d+/);
        if (match) {
          const num = parseInt(match[0], 10);
          if (!isNaN(num) && num > maxNum) {
            maxNum = num;
          }
        }
      }
    });
    return `DJ-${maxNum + 1}`;
  };

  // Helper functions to send email via backend API
  const sendApiVerificationCode = async (targetEmail: string, code: string, targetName?: string) => {
    try {
      const res = await fetch('/api/auth/send-verification-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: targetEmail, code, name: targetName })
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (err) {
      console.warn('[Email Verification] Failed to send via backend API:', err);
    }
    return { success: true, simulated: true };
  };

  const sendApiPasswordResetCode = async (targetEmail: string, code: string) => {
    try {
      const res = await fetch('/api/auth/send-reset-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: targetEmail, code })
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (err) {
      console.warn('[Password Reset] Failed to send via backend API:', err);
    }
    return { success: true, simulated: true };
  };

  const handleSendVerificationCode = (targetEmail: string, targetName?: string) => {
    // Generate 6-digit code
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedCode(code);
    sendApiVerificationCode(targetEmail, code, targetName);
    return code;
  };

  const handleSendPasswordResetCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfoMessage(null);

    if (!forgotEmail || !forgotEmail.trim()) {
      setError('Por favor informe o seu e-mail cadastrado.');
      return;
    }

    const cleanEmail = forgotEmail.trim().toLowerCase();
    let existingUser = demoUsers.find((u) => u && u.email && u.email.trim().toLowerCase() === cleanEmail);

    if (!existingUser) {
      try {
        const usersSnap = await getDocs(collection(db, 'users'));
        usersSnap.forEach((d) => {
          const uData = d.data() as UserProfile;
          if (uData.email && uData.email.trim().toLowerCase() === cleanEmail) {
            existingUser = { uid: d.id, ...uData };
          }
        });
      } catch (errCheck) {
        console.warn('Verificação de e-mail no Firestore para reset:', errCheck);
      }
    }

    if (!existingUser) {
      try {
        const apiRes = await fetch('/api/data/users');
        const apiData = await apiRes.json();
        if (apiData.success && Array.isArray(apiData.users)) {
          const found = apiData.users.find((u: UserProfile) => u && u.email && u.email.trim().toLowerCase() === cleanEmail);
          if (found) existingUser = found;
        }
      } catch (apiErr) {
        console.warn('Verificação de e-mail na API para reset:', apiErr);
      }
    }

    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setResetCodeGenerated(code);
    setResetUser(existingUser || null);
    setMode('reset_password');
    setInfoMessage(`Código de recuperação enviado para ${cleanEmail}. Verifique seu e-mail.`);
    
    // Send email via backend SMTP if configured
    sendApiPasswordResetCode(cleanEmail, code);
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfoMessage(null);

    if (!newPassword || newPassword.trim().length < 5) {
      setError('A nova senha deve ter no mínimo 5 caracteres.');
      return;
    }

    if (newPassword.trim() !== confirmNewPassword.trim()) {
      setError('As senhas digitadas não coincidem.');
      return;
    }

    if (!resetCodeInput || resetCodeInput.trim() !== resetCodeGenerated) {
      setError('Código de confirmação inválido ou não informado. Digite o código de 6 dígitos enviado para seu e-mail.');
      return;
    }

    setLoading(true);

    try {
      const cleanEmail = forgotEmail.trim().toLowerCase();
      const isAdminEmail = cleanEmail === 'egami.corporation@gmail.com' || cleanEmail.includes('admin');
      
      let targetUser: UserProfile;

      if (resetUser) {
        targetUser = {
          ...resetUser,
          password: newPassword.trim(),
          role: isAdminEmail ? 'admin' : (resetUser.role || 'client'),
          suiteNumber: isAdminEmail ? 'DJ-ADMIN' : (resetUser.suiteNumber || getNextSuiteNumber())
        };
      } else {
        targetUser = {
          uid: isAdminEmail ? 'admin-egami-001' : `uid-${Date.now()}`,
          email: cleanEmail,
          role: isAdminEmail ? 'admin' : 'client',
          suiteNumber: isAdminEmail ? 'DJ-ADMIN' : getNextSuiteNumber(),
          name: cleanEmail.split('@')[0],
          createdAt: new Date().toISOString(),
          password: newPassword.trim(),
          emailVerified: true
        };
      }

      // Update demoUsers array immediately in state and local storage so login succeeds instantly!
      const updatedList = demoUsers.map((u) =>
        (u?.email || '').toLowerCase() === cleanEmail || u?.uid === targetUser.uid ? targetUser : u
      );
      if (!updatedList.some((u) => (u?.email || '').toLowerCase() === cleanEmail || u?.uid === targetUser.uid)) {
        updatedList.push(targetUser);
      }

      if (onUsersUpdate) {
        onUsersUpdate(updatedList);
      }

      // Save updated password in Firestore if available
      try {
        await setDoc(doc(db, 'users', targetUser.uid), targetUser, { merge: true });
      } catch (dbErr) {
        console.warn('Salvo no armazenamento local:', dbErr);
      }

      setLoading(false);
      
      // Log in directly with the newly set password profile!
      onLoginSuccess(targetUser);
      onClose();
    } catch (err) {
      console.error('Erro ao redefinir senha:', err);
      setError('Ocorreu um erro ao salvar sua nova senha. Tente novamente.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfoMessage(null);

    if (!email || !password) {
      setError('Por favor preencha todos os campos obrigatórios.');
      return;
    }

    // Bot domain check
    const domain = email.split('@')[1]?.toLowerCase();
    if (domain && DISPOSABLE_EMAIL_DOMAINS.includes(domain)) {
      setError('E-mails temporários/descartáveis não são permitidos. Use um e-mail válido.');
      return;
    }

    if (mode === 'register') {
      if (!name || !name.trim()) {
        setError('Por favor, informe seu nome completo.');
        return;
      }
      if (!registerPhone || !registerPhone.trim()) {
        setError('Por favor, informe seu número de telefone.');
        return;
      }
      if (!isHuman) {
        setError('Por favor, confirme a verificação de proteção contra robôs (Sou Humano).');
        return;
      }
    }

    setLoading(true);
    try {
      if (mode === 'register') {
        const cleanRegisterEmail = email.trim().toLowerCase();
        if (cleanRegisterEmail === MASTER_ADMIN_USER.email.toLowerCase() || cleanRegisterEmail === 'egami.corporation@gmail.com') {
          setError('Este e-mail pertence à Administração do sistema. Acesse a aba "Entrar" e informe sua senha de administrador.');
          setLoading(false);
          return;
        }

        // Check if email already exists in local demoUsers or in Firestore
        let existingAccount = demoUsers.find(
          (u) => u.email && u.email.trim().toLowerCase() === cleanRegisterEmail
        );

        if (!existingAccount) {
          try {
            const usersSnap = await getDocs(collection(db, 'users'));
            usersSnap.forEach((d) => {
              const uData = d.data() as UserProfile;
              if (uData.email && uData.email.trim().toLowerCase() === cleanRegisterEmail) {
                existingAccount = { uid: d.id, ...uData };
              }
            });
          } catch (errCheck) {
            console.warn('Verificação de e-mail existente no Firestore:', errCheck);
          }
        }

        if (existingAccount) {
          const suiteLabel = existingAccount.suiteNumber ? ` (Suíte ${existingAccount.suiteNumber})` : '';
          setError(
            `O e-mail "${cleanRegisterEmail}" já está cadastrado no sistema${suiteLabel}. Não é permitido cadastrar o mesmo e-mail novamente em outra suíte.`
          );
          setLoading(false);
          return;
        }

        const generatedSuite = role === 'admin' ? 'DJ-ADMIN' : getNextSuiteNumber();
        const userUid = `uid-${Date.now()}`;
        const targetName = name.trim() || email.split('@')[0];
        const code = handleSendVerificationCode(email.trim(), targetName);

        let cleanPhone = registerPhone ? String(registerPhone).trim() : '';
        if (cleanPhone) {
          const rawDigits = cleanPhone.replace(/\D/g, '');
          cleanPhone = '+' + rawDigits;
          setWhatsappPhone(cleanPhone);
        }

        const newUser: UserProfile = {
          uid: userUid,
          email: email.trim(),
          role,
          suiteNumber: generatedSuite,
          name: targetName,
          phone: cleanPhone || undefined,
          createdAt: new Date().toISOString(),
          password: password.trim(),
          emailVerified: true,
          verificationCode: code
        };

        // Save user directly to Firestore
        try {
          await setDoc(doc(db, 'users', newUser.uid), newUser, { merge: true });
        } catch (saveErr) {
          console.warn('Salvo no estado local:', saveErr);
        }

        if (referrerSuiteCode && referrerSuiteCode.trim()) {
          try {
            await registerReferralForNewUser({
              newUser,
              referrerSuiteInput: referrerSuiteCode,
              allUsers: demoUsers
            });
          } catch (refErr) {
            console.warn('Notice registering referral:', refErr);
          }
        }

        if (onUsersUpdate) {
          onUsersUpdate([...demoUsers, newUser]);
        }

        setPendingUser(newUser);
        setInputCode('');
        setLoading(false);
        setMode('verify');
        setInfoMessage(`Cadastro realizado com sucesso! Um e-mail com o código de confirmação de 6 dígitos foi enviado para ${email.trim()}. Insira o código abaixo para ativar sua Suíte.`);
      } else if (mode === 'login') {
        // Login mode: search existing or create profile
        const cleanEmailInput = email.trim().toLowerCase();
        const cleanPasswordInput = password.trim();

        const isMasterAdminEmail = cleanEmailInput === MASTER_ADMIN_USER.email.toLowerCase() || cleanEmailInput === 'egami.corporation@gmail.com';

        let existing: UserProfile | undefined;

        if (isMasterAdminEmail) {
          // Always resolve to the master admin account with role: 'admin'
          const foundInList = demoUsers.find(
            (u) => (u?.uid === MASTER_ADMIN_USER.uid || u?.role === 'admin') && (u?.email || '').toLowerCase() === cleanEmailInput
          ) || demoUsers.find((u) => u?.uid === MASTER_ADMIN_USER.uid || u?.role === 'admin');

          // Strict validation: Require the exact admin password Reiko.8879
          const expectedAdminPassword = (foundInList?.password && foundInList.password !== 'teste12345' && foundInList.password !== 'admin123' && foundInList.password !== '123456')
            ? foundInList.password
            : MASTER_ADMIN_USER.password;

          if (cleanPasswordInput !== expectedAdminPassword) {
            setError('Senha incorreta para a conta de Administrador. O acesso ao painel administrativo requer a senha de segurança autorizada.');
            setLoading(false);
            return;
          }

          existing = {
            ...(foundInList || {}),
            ...MASTER_ADMIN_USER,
            role: 'admin',
            suiteNumber: 'DJ-ADMIN',
            uid: MASTER_ADMIN_USER.uid,
            password: expectedAdminPassword
          };

          // Clean up duplicate non-admin profiles with egami.corporation@gmail.com
          const nonAdminDups = demoUsers.filter(
            (u) => (u?.email || '').toLowerCase() === cleanEmailInput && u?.uid !== MASTER_ADMIN_USER.uid
          );
          if (nonAdminDups.length > 0) {
            nonAdminDups.forEach((dup) => {
              try {
                deleteDoc(doc(db, 'users', dup.uid)).catch(() => {});
              } catch (e) {}
            });
          }

          const sanitizedList = [
            existing,
            ...demoUsers.filter(
              (u) => u?.uid !== MASTER_ADMIN_USER.uid && (u?.email || '').toLowerCase() !== cleanEmailInput
            )
          ];
          if (onUsersUpdate) onUsersUpdate(sanitizedList);

          try {
            setDoc(doc(db, 'users', MASTER_ADMIN_USER.uid), existing, { merge: true }).catch(() => {});
          } catch (e) {}
        } else {
          existing = demoUsers.find((u) => u && u.email && u.email.trim().toLowerCase() === cleanEmailInput);
          
          // If not found in demoUsers, check Firestore
          if (!existing) {
            try {
              const usersSnap = await getDocs(collection(db, 'users'));
              usersSnap.forEach((d) => {
                const uData = d.data() as UserProfile;
                if (uData && uData.email && uData.email.trim().toLowerCase() === cleanEmailInput) {
                  existing = { uid: d.id, ...uData };
                }
              });
            } catch (e) {
              console.warn('Busca no Firestore:', e);
            }
          }

          // If still not found, check server persistent storage
          if (!existing) {
            try {
              const apiRes = await fetch('/api/data/users');
              const apiData = await apiRes.json();
              if (apiData.success && Array.isArray(apiData.users)) {
                const found = apiData.users.find((u: UserProfile) => u && u.email && u.email.trim().toLowerCase() === cleanEmailInput);
                if (found) existing = found;
              }
            } catch (apiErr) {
              console.warn('Busca de usuário na API do servidor:', apiErr);
            }
          }
        }

        if (existing) {
          const isAdminAcc = isMasterAdminEmail || existing.role === 'admin';

          if (!isMasterAdminEmail) {
            const isPasswordValid = !existing.password || existing.password.trim() === cleanPasswordInput;
            if (!isPasswordValid) {
              setError('Senha incorreta. Verifique a senha informada ou clique em "Esqueci minha senha" abaixo para redefinir seu acesso.');
              setLoading(false);
              return;
            }
          }

          // Force role admin for admin email
          if (isAdminAcc) {
            existing = {
              ...existing,
              role: 'admin',
              suiteNumber: 'DJ-ADMIN',
              uid: MASTER_ADMIN_USER.uid
            };
          }

          // If user has MFA enabled, prompt for MFA code verification
          if (existing.mfaEnabled) {
            const code = handleSendVerificationCode(existing.email, existing.name);
            const cleanPhone = existing.mfaPhone || existing.phone || whatsappPhone;
            if (cleanPhone) {
              sendWhatsAppNotification(cleanPhone, 'Código MFA', `Seu código MFA é: *${code}*`)
                .catch((e) => console.warn('Falha envio WhatsApp MFA login:', e));
            }
            setPendingUser({ ...existing, verificationCode: code });
            setInputCode('');
            setLoading(false);
            setMode('verify');
            setInfoMessage(`Insira o código de 6 dígitos enviado para o seu e-mail (${existing.email}) para acessar sua Suíte.`);
            return;
          }

          setLoading(false);
          try {
            onLoginSuccess(existing);
          } catch (loginErr) {
            console.warn('Notice during onLoginSuccess:', loginErr);
          }
          onClose();
        } else {
          // No user found with this email
          setError('Nenhum cadastro encontrado com este e-mail. Caso ainda não tenha uma conta, clique em "Não tem conta? Cadastrar" acima para criar sua Suíte.');
          setLoading(false);
          return;
        }
      }
    } catch (err: any) {
      console.error('Erro na autenticação:', err);
      const customMsg = err?.message && typeof err.message === 'string' && !err.message.includes('object')
        ? `Erro ao autenticar: ${err.message}`
        : 'Não foi possível validar suas credenciais. Verifique os dados digitados ou clique em "Esqueci minha senha".';
      setError(customMsg);
      setLoading(false);
    }
  };

  // Verify Code Submission
  const handleVerifyCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!pendingUser) {
      setError('Sessão de verificação expirada. Tente novamente.');
      return;
    }

    if (!inputCode || !inputCode.trim()) {
      setError('Por favor, digite o código de 6 dígitos recebido por e-mail.');
      return;
    }

    const cleanInput = inputCode.trim();
    const expectedCode = generatedCode || pendingUser.verificationCode;

    if (expectedCode && cleanInput !== expectedCode) {
      setError('Código de verificação incorreto. Verifique a caixa de entrada/spam do seu e-mail e tente novamente.');
      return;
    }

    setLoading(true);

    const verifiedUser: UserProfile = {
      ...pendingUser,
      emailVerified: true,
      verificationCode: undefined
    };

    try {
      await setDoc(doc(db, 'users', verifiedUser.uid), verifiedUser, { merge: true });
    } catch (e) {
      console.warn('Salvo localmente:', e);
    }

    setLoading(false);
    onLoginSuccess(verifiedUser);
    onClose();
  };

  const handleResendCode = () => {
    if (!pendingUser) return;
    const newCode = handleSendVerificationCode(pendingUser.email);
    setInputCode('');
    setError(null);

    const targetPhone = pendingUser.mfaPhone || whatsappPhone || pendingUser.phone;
    if (targetPhone) {
      sendWhatsAppNotification(targetPhone, 'Novo Código de Ativação (MFA)', `Seu novo código de ativação Do Japão é: *${newCode}*`)
        .catch((e) => console.warn('Falha no reenvio WhatsApp MFA:', e));
      setInfoMessage(`Novo código enviado para o Smartphone (${targetPhone}) e para ${pendingUser.email}.`);
    } else {
      setInfoMessage(`Novo código de confirmação enviado para ${pendingUser.email}.`);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in overflow-y-auto">
      <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl overflow-hidden border border-slate-100 my-8">
        
        {/* Top Header */}
        <div className="bg-slate-900 text-white p-6 relative overflow-hidden text-center">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white rounded-xl transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex justify-center mb-3">
            <Logo size="lg" />
          </div>
          <p className="text-slate-400 text-xs sm:text-sm">
            {mode === 'verify' 
              ? 'Verificação de Segurança por E-mail'
              : mode === 'forgot_password'
                ? 'Recuperação de Acesso à Conta'
                : mode === 'reset_password'
                  ? 'Redefinição de Senha de Acesso'
                  : mode === 'login' 
                    ? 'Acesse seu painel exclusivo de cliente Do Japão' 
                    : 'Crie sua conta e receba no conforto de sua casa os melhores produtos diretamente do Japão'}
          </p>
        </div>

        {/* MODE: FORGOT PASSWORD */}
        {mode === 'forgot_password' ? (
          <form onSubmit={handleSendPasswordResetCode} className="p-6 space-y-4">
            <div className="text-center space-y-2">
              <div className="inline-flex p-3 bg-amber-100 text-amber-800 rounded-2xl mb-1">
                <KeyRound className="w-6 h-6 text-amber-700" />
              </div>
              <h3 className="text-base font-extrabold text-slate-900">
                Esqueci Minha Senha
              </h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Digite o seu e-mail cadastrado para enviarmos um código de 6 dígitos de redefinição de senha.
              </p>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-xl text-xs border border-red-200">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                E-mail Cadastrado
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="seu@email.com"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-sky-500 text-slate-900"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#1D3557] hover:bg-[#152742] text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-2"
            >
              <KeyRound className="w-4 h-4 text-amber-400" />
              <span>Enviar Código de Recuperação</span>
            </button>

            <div className="text-center pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  setError(null);
                  setInfoMessage(null);
                }}
                className="text-xs font-bold text-sky-600 hover:underline inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Voltar para o Login</span>
              </button>
            </div>
          </form>
        ) : mode === 'reset_password' ? (
          /* MODE: RESET PASSWORD WITH CODE */
          <form onSubmit={handleResetPasswordSubmit} className="p-6 space-y-4">
            <div className="text-center space-y-2">
              <div className="inline-flex p-3 bg-emerald-100 text-emerald-800 rounded-2xl mb-1">
                <ShieldCheck className="w-6 h-6 text-emerald-700" />
              </div>
              <h3 className="text-base font-extrabold text-slate-900">
                Redefinir Sua Senha
              </h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Informe o código enviado para <strong className="text-slate-900">{forgotEmail}</strong> e digite sua nova senha.
              </p>
            </div>

            {infoMessage && (
              <div className="p-3 bg-emerald-50 text-emerald-800 rounded-xl text-xs font-semibold flex items-center gap-2 border border-emerald-200">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>{infoMessage}</span>
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-xl text-xs border border-red-200">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {resetCodeGenerated && (
              <div className="p-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-2xl space-y-1.5 shadow-xs">
                <div className="flex items-center justify-between text-amber-950 font-extrabold text-xs">
                  <span className="flex items-center gap-1.5">
                    <Key className="w-3.5 h-3.5 text-amber-600" />
                    Código de Recuperação:
                  </span>
                  <span className="font-mono text-sm tracking-widest text-amber-950 bg-amber-200 border border-amber-300 px-2 py-0.5 rounded-md font-black select-all">
                    {resetCodeGenerated}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 pt-1 border-t border-amber-200/70 text-[10px] text-amber-800">
                  <span>Não recebeu por e-mail? Preencha diretamente:</span>
                  <button
                    type="button"
                    onClick={() => setResetCodeInput(resetCodeGenerated)}
                    className="px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-lg transition-all text-[10px] flex items-center gap-1 shrink-0"
                  >
                    <Zap className="w-3 h-3 text-amber-200" />
                    <span>Preencher</span>
                  </button>
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Código de Verificação de 6 Dígitos
              </label>
              <input
                type="text"
                value={resetCodeInput}
                onChange={(e) => setResetCodeInput(e.target.value.trim())}
                placeholder="Ex: 849201"
                maxLength={6}
                className="w-full text-center tracking-widest text-lg font-mono font-black py-2.5 rounded-xl border-2 border-amber-300 focus:border-amber-600 text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 bg-slate-50"
                required
              />
            </div>

            {/* Enviar Código por WhatsApp */}
            <div className="p-3 bg-emerald-50 border border-emerald-200/80 rounded-2xl space-y-2">
              <div className="flex items-center justify-between text-emerald-900 font-extrabold text-[11px]">
                <span className="flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5 text-emerald-600" />
                  Receber Código via WhatsApp
                </span>
                <span className="text-[10px] text-emerald-700 bg-emerald-200/60 px-2 py-0.5 rounded-full font-bold">
                  Envio Direto
                </span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <div className="flex-1">
                  <CountryPhoneInput
                    id="forgot-password-phone"
                    value={whatsappPhone}
                    onChange={setWhatsappPhone}
                    defaultCountry="BR"
                    size="sm"
                    placeholder="Celular / WhatsApp"
                  />
                </div>
                <button
                  type="button"
                  disabled={isSendingWhatsApp}
                  onClick={() => handleSendWhatsAppCode(whatsappPhone, resetCodeGenerated, true)}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-1 shrink-0 disabled:opacity-50 cursor-pointer h-[34px]"
                >
                  {isSendingWhatsApp ? <RefreshCw className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />}
                  <span>Enviar</span>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Nova Senha
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Nova senha (mínimo 5 caracteres)"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-sky-500 text-slate-900"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Confirmar Nova Senha
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  placeholder="Repita a nova senha"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-sky-500 text-slate-900"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 text-white" />
                  <span>Redefinir Senha e Salvar</span>
                </>
              )}
            </button>

            <div className="flex items-center justify-between text-xs pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => {
                  const code = Math.floor(100000 + Math.random() * 900000).toString();
                  setResetCodeGenerated(code);
                  setResetCodeInput('');
                  setInfoMessage(`Novo código enviado para ${forgotEmail.trim()}. Verifique seu e-mail.`);
                  sendApiPasswordResetCode(forgotEmail.trim(), code);
                }}
                className="font-bold text-amber-600 hover:underline flex items-center gap-1"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Reenviar Código</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  setError(null);
                  setInfoMessage(null);
                }}
                className="text-slate-400 hover:text-slate-600 font-medium"
              >
                Cancelar
              </button>
            </div>
          </form>
        ) : mode === 'verify' ? (
          <form onSubmit={handleVerifyCodeSubmit} className="p-6 space-y-4">
            <div className="text-center space-y-2">
              <div className="inline-flex p-3 bg-gradient-to-br from-emerald-100 to-sky-100 text-emerald-800 rounded-2xl mb-1 border border-emerald-200">
                <ShieldCheck className="w-6 h-6 text-emerald-700" />
              </div>
              <h3 className="text-base font-extrabold text-slate-900">
                Ativação da Suíte — Código de Confirmação
              </h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Enviamos um código de confirmação de 6 dígitos para o e-mail <strong className="text-slate-900">{pendingUser?.email}</strong>. Insira o código abaixo para ativar sua Suíte.
              </p>
            </div>

            {infoMessage && (
              <div className="p-3 bg-sky-50 text-sky-800 rounded-xl text-xs font-semibold flex items-center gap-2 border border-sky-200">
                <CheckCircle2 className="w-4 h-4 text-sky-600 shrink-0" />
                <span>{infoMessage}</span>
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-xl text-xs border border-red-200">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 text-center">
                Código de Verificação de 6 Dígitos
              </label>
              <input
                type="text"
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value.trim())}
                placeholder="Ex: 849201"
                maxLength={6}
                className="w-full text-center tracking-widest text-lg font-mono font-black py-3 rounded-xl border-2 border-sky-300 focus:border-sky-600 text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500 bg-slate-50"
                required
              />
            </div>

            {/* Enviar Código por WhatsApp */}
            <div className="p-3 bg-emerald-50 border border-emerald-200/80 rounded-2xl space-y-2">
              <div className="flex items-center justify-between text-emerald-900 font-extrabold text-[11px]">
                <span className="flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5 text-emerald-600" />
                  Receber Código via WhatsApp
                </span>
                <span className="text-[10px] text-emerald-700 bg-emerald-200/60 px-2 py-0.5 rounded-full font-bold">
                  Envio Direto
                </span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <div className="flex-1">
                  <CountryPhoneInput
                    id="register-whatsapp-phone"
                    value={whatsappPhone}
                    onChange={setWhatsappPhone}
                    defaultCountry="BR"
                    size="sm"
                    placeholder="Celular / WhatsApp"
                  />
                </div>
                <button
                  type="button"
                  disabled={isSendingWhatsApp}
                  onClick={() => handleSendWhatsAppCode(whatsappPhone, generatedCode, false)}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-1 shrink-0 disabled:opacity-50 cursor-pointer h-[34px]"
                >
                  {isSendingWhatsApp ? <RefreshCw className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />}
                  <span>Enviar</span>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 text-white" />
                  <span>Verificar E-mail e Ativar Suíte</span>
                </>
              )}
            </button>

            <div className="flex items-center justify-between text-xs pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={handleResendCode}
                className="font-bold text-sky-600 hover:underline flex items-center gap-1"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Reenviar E-mail de Confirmação</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setMode('register');
                  setError(null);
                  setInfoMessage(null);
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                Alterar E-mail
              </button>
            </div>
          </form>
        ) : (
          /* Traditional Email/Password Form */
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="flex justify-between items-center pb-2 border-b border-slate-100">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                {mode === 'login' ? 'Acessar com e-mail' : 'Criar nova conta de cliente'}
              </span>
              <button
                type="button"
                onClick={() => {
                  setMode(mode === 'login' ? 'register' : 'login');
                  setError(null);
                  setInfoMessage(null);
                }}
                className="text-xs font-bold text-sky-600 hover:underline"
              >
                {mode === 'login' ? 'Não tem conta? Cadastrar' : 'Já tem conta? Entrar'}
              </button>
            </div>

            {infoMessage && (
              <div className="p-3 bg-emerald-50 text-emerald-800 rounded-xl text-xs font-semibold flex items-center gap-2 border border-emerald-200">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>{infoMessage}</span>
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-xl text-xs border border-red-200 space-y-2">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span className="leading-relaxed font-semibold">{error}</span>
                </div>
                {error.includes('já está cadastrado') && (
                  <div className="flex items-center gap-2 pt-1 border-t border-red-200/60">
                    <button
                      type="button"
                      onClick={() => {
                        setMode('login');
                        setError(null);
                      }}
                      className="px-2.5 py-1 bg-sky-600 hover:bg-sky-700 text-white rounded-lg font-bold text-[11px] transition-all cursor-pointer"
                    >
                      Acessar Conta (Entrar)
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setForgotEmail(email);
                        setMode('forgot_password');
                        setError(null);
                      }}
                      className="px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-bold text-[11px] transition-all cursor-pointer"
                    >
                      Esqueci Minha Senha
                    </button>
                  </div>
                )}
                {error.includes('Nenhum cadastro encontrado') && (
                  <div className="pt-1 border-t border-red-200/60">
                    <button
                      type="button"
                      onClick={() => {
                        setMode('register');
                        setError(null);
                      }}
                      className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-[11px] transition-all cursor-pointer"
                    >
                      Criar Minha Suíte (Cadastrar)
                    </button>
                  </div>
                )}
              </div>
            )}

            {mode === 'register' && (
              <>
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Nome Completo</label>
                  <div className="relative">
                    <User className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Seu Nome Completo"
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-sky-500 text-slate-900"
                      required
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                      Número de Telefone / Celular
                    </label>
                  </div>

                  <div className="relative">
                    <CountryPhoneInput
                      id="register-phone-field"
                      defaultCountry="BR"
                      value={registerPhone}
                      onChange={(val) => setRegisterPhone(val ? String(val) : '')}
                      placeholder="DDD + Celular"
                      showValidationHint
                      size="md"
                    />
                  </div>
                  <p className="text-[10px] text-slate-500 font-medium mt-1.5 flex items-center gap-1">
                    🌍 <span>Selecione a bandeira do seu país para preencher o código internacional e formatar o número.</span>
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Suíte de Quem Indicou (Opcional · Ganhe ¥500 no 1º Frete)
                  </label>
                  <input
                    type="text"
                    value={referrerSuiteCode}
                    onChange={(e) => setReferrerSuiteCode(e.target.value)}
                    placeholder="Ex: DJ-24950"
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-xs font-mono uppercase font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500 text-slate-900"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">E-mail</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-sky-500 text-slate-900"
                  required
                />
              </div>
              {mode === 'register' && email.trim().length > 3 && demoUsers.some(u => u.email && u.email.trim().toLowerCase() === email.trim().toLowerCase()) && (
                <div className="mt-1.5 p-2 bg-amber-50 border border-amber-200 rounded-xl text-[11px] text-amber-900 flex items-center justify-between gap-2 animate-fadeIn">
                  <div className="flex items-center gap-1.5 font-medium">
                    <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                    <span>Este e-mail já possui uma Suíte cadastrada no sistema.</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setMode('login');
                      setError(null);
                    }}
                    className="text-[11px] font-bold text-amber-800 underline hover:text-amber-900 shrink-0 cursor-pointer"
                  >
                    Entrar
                  </button>
                </div>
              )}
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Senha</label>
                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={() => {
                      setForgotEmail(email);
                      setMode('forgot_password');
                      setError(null);
                      setInfoMessage(null);
                    }}
                    className="text-xs font-bold text-sky-600 hover:underline"
                  >
                    Esqueci minha senha
                  </button>
                )}
              </div>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-sky-500 text-slate-900"
                  required
                />
              </div>
            </div>

            {/* Anti-Bot Verification Checkbox for Registration */}
            {mode === 'register' && (
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={isHuman}
                    onChange={(e) => setIsHuman(e.target.checked)}
                    className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 cursor-pointer"
                  />
                  <span className="text-xs font-bold text-slate-700">Sou humano (Proteção Anti-Bot)</span>
                </label>

                <div className="flex items-center gap-1 text-[10px] text-slate-400 font-medium">
                  <Bot className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Protegido</span>
                </div>
              </div>
            )}

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#1D3557] hover:bg-[#152742] text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <UserCheck className="w-4 h-4 text-emerald-400" />
                    <span>{mode === 'login' ? 'Entrar no Painel' : 'Enviar E-mail de Confirmação'}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}

      </div>
    </div>
  );
};

export default AuthModal;
